import { Box, Typography, useTheme } from "@mui/material";
import { FC, useMemo, useState } from "react";
import { CustomTabs, TabItem } from "../../../../components/CustomTabs";
import type { Dimension } from "../../../../domain/models/metrics";
import { useIsMobile } from "../../../../hooks/useIsMobile";
import { useLocalization } from "../../../../hooks/useLocalization";
import {
  DIMENSION_TO_TAB_MAP,
  SELECTED_TAB_COMPONENTS,
  TAB_TO_DIMENSION_MAP,
  TimeRangeType,
} from "../constants";
import { DateRangeActions } from "./DateRangeActions";
import { TimeFilterWrapper } from "./TimeFilterWrapper";

interface TimeFilterProps {
  startDate: Date;
  endDate: Date;
  setStartDate: (date: Date) => void;
  setEndDate: (date: Date) => void;
  earliestSelectableDate: Date;
  dimension?: Dimension;
  onDimensionChange?: (dimension: Dimension) => void;
  defaultTab?: TimeRangeType;
  formatDateRange?: (startDate: Date, endDate: Date) => string;
}

const defaultFormatDate = (date: Date) => {
  const day = date.getUTCDate();
  const month = date.toLocaleDateString("en-US", {
    month: "short",
    timeZone: "UTC",
  });
  const year = date.getUTCFullYear();

  return `${day} ${month} ${year}`;
};

const defaultFormatDateRange = (start: Date, end: Date) =>
  `${defaultFormatDate(start)} - ${defaultFormatDate(end)}`;

const resolveInitialTab = (
  dimension: Dimension | undefined,
  fallback: TimeRangeType,
): TimeRangeType =>
  dimension === undefined ? fallback : DIMENSION_TO_TAB_MAP[dimension];

export const TimeFilter: FC<TimeFilterProps> = ({
  startDate,
  endDate,
  setStartDate,
  setEndDate,
  earliestSelectableDate,
  dimension,
  onDimensionChange,
  defaultTab = TimeRangeType.Months,
  formatDateRange = defaultFormatDateRange,
}) => {
  const theme = useTheme();
  const isMobile = useIsMobile();
  const { t } = useLocalization();

  const initialTab = resolveInitialTab(dimension, defaultTab);

  const [timeFilterAnchor, setTimeFilterAnchor] = useState<HTMLElement | null>(
    null,
  );

  const [isTimeFilterDialogOpen, setIsTimeFilterDialogOpen] = useState(false);

  const [tempStartDate, setTempStartDate] = useState<Date | null>(null);
  const [tempEndDate, setTempEndDate] = useState<Date | null>(null);

  const isTimeFilterOpen = Boolean(timeFilterAnchor) || isTimeFilterDialogOpen;

  const [activeTab, setActiveTab] = useState<TimeRangeType>(initialTab);

  const ActiveTabView = useMemo(
    () => SELECTED_TAB_COMPONENTS[activeTab],
    [activeTab],
  );

  const handleTabChange = (_event: React.SyntheticEvent, newValue: string) => {
    setActiveTab(newValue as TimeRangeType);
    setTempStartDate(null);
    setTempEndDate(null);
  };

  const handleTimeFilterOpen = (event: React.MouseEvent<HTMLButtonElement>) => {
    setTempStartDate(startDate);
    setTempEndDate(endDate);
    if (isMobile) {
      setIsTimeFilterDialogOpen(true);
    } else {
      setTimeFilterAnchor(event.currentTarget);
    }
  };

  const handleTimeFilterClose = () => {
    setTimeFilterAnchor(null);
    setIsTimeFilterDialogOpen(false);
    setActiveTab(initialTab);
  };

  const handleRemoveStartDate = () => {
    setTempStartDate(null);
    setTempEndDate(null);
  };

  const handleRemoveEndDate = () => {
    setTempEndDate(null);
  };

  const handleApply = () => {
    setStartDate(tempStartDate ?? startDate);
    setEndDate(tempEndDate ?? endDate);
    onDimensionChange?.(TAB_TO_DIMENSION_MAP[activeTab]);
    setTimeFilterAnchor(null);
    setIsTimeFilterDialogOpen(false);
  };

  const tabItems: TabItem[] = useMemo(
    () => [
      {
        label: t("pages.dataAnalytics.timeFilter.weeks"),
        value: TimeRangeType.Weeks,
      },
      {
        label: t("pages.dataAnalytics.timeFilter.months"),
        value: TimeRangeType.Months,
      },
      {
        label: t("pages.dataAnalytics.timeFilter.quarters"),
        value: TimeRangeType.Quarters,
      },
      {
        label: t("pages.dataAnalytics.timeFilter.years"),
        value: TimeRangeType.Years,
      },
    ],
    [t],
  );

  return (
    <Box
      sx={{
        display: "flex",
        alignItems: "center",
        gap: 3,
        justifyContent: { xs: "space-between", md: "flex-start" },
      }}
    >
      <Typography
        sx={{
          color: theme.colors.text[5],
          fontSize: { xs: 18, md: 20 },
          fontWeight: 700,
        }}
      >
        {formatDateRange(startDate, endDate)}
      </Typography>
      <TimeFilterWrapper
        handleTimeFilterClose={handleTimeFilterClose}
        handleTimeFilterOpen={handleTimeFilterOpen}
        isApplyDisabled={!(tempStartDate && tempEndDate)}
        isTimeFilterOpen={isTimeFilterOpen}
        onApply={handleApply}
        timeFilterAnchor={timeFilterAnchor}
      >
        <Box sx={{ px: 2, pt: 2 }}>
          <CustomTabs
            activeTab={activeTab}
            containerSx={{ marginBottom: "16px" }}
            onTabChange={handleTabChange}
            tabWidth="25%"
            tabs={tabItems}
          />
        </Box>
        <Box sx={{ minHeight: 380 }}>
          <ActiveTabView
            earliestSelectableDate={earliestSelectableDate}
            setTempEndDate={setTempEndDate}
            setTempStartDate={setTempStartDate}
            tempEndDate={tempEndDate}
            tempStartDate={tempStartDate}
          />
        </Box>
        <DateRangeActions
          endDate={tempEndDate}
          formatDate={defaultFormatDate}
          onRemoveEndDate={handleRemoveEndDate}
          onRemoveStartDate={handleRemoveStartDate}
          startDate={tempStartDate}
        />
      </TimeFilterWrapper>
    </Box>
  );
};
